// ─────────────────────────────────────────────────────────────────────────
// `/p/<landingSlug>` — the public product page.
//
// Phase 9 of docs/plans/2026-08-25-sku-shelf-portal-plan.md.
//
// The address lives on the MARKETING host, not the portal: this is a page for
// somebody who has never signed in, and it should sit on the same domain as
// everything else a stranger reads. `MarketingV2App` dispatches to the landing
// surface the same way it already dispatches `/score` and `/legal/<slug>`.
//
//   /p/ai-website-studio               the page
//   /p/ai-website-studio?state=thanks  where Stripe sends a buyer back to
//   ?view=landing&p=<slug>             local dev, from the default surface
//   &landingPreview=<state>            DEV fixtures (see landingFixtures)
//
// No router dependency, matching every other surface in this repo.
// ─────────────────────────────────────────────────────────────────────────

export type LandingViewState = 'offer' | 'thanks';

export interface LandingRoute {
  slug: string;
  state: LandingViewState;
}

const PREFIX = '/p/';

/** A landing slug is a catalog-style slug and nothing else. */
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * Is this URL a landing page at all? Called by the marketing surface before it
 * renders anything, so it has to be cheap and total.
 */
export function isLandingPath(pathname: string, search = ''): boolean {
  if (pathname.startsWith(PREFIX)) return true;
  return new URLSearchParams(search).get('view') === 'landing';
}

/**
 * Parse the address. An unparseable one returns an empty slug, which the
 * surface renders as its not-found page — never a blank frame and never a
 * request for something that cannot exist.
 */
export function parseLandingRoute(pathname: string, search = ''): LandingRoute {
  const params = new URLSearchParams(search);
  const fromPath = pathname.startsWith(PREFIX)
    ? decodeURIComponent(pathname.slice(PREFIX.length)).replace(/\/+$/, '')
    : '';
  const raw = (fromPath || params.get('p') || '').trim().toLowerCase();
  const slug = SLUG_PATTERN.test(raw) ? raw : '';

  return {
    slug,
    state: params.get('state') === 'thanks' ? 'thanks' : 'offer',
  };
}

/** Absolute URL for a landing state — what Stripe is handed as a return address. */
export function landingUrl(slug: string, state: LandingViewState, origin: string, search = ''): string {
  const preview = new URLSearchParams(search).get('landingPreview');
  const params = new URLSearchParams();
  if (state === 'thanks') params.set('state', 'thanks');
  if (preview) params.set('landingPreview', preview);
  const query = params.toString();
  return `${origin.replace(/\/$/, '')}${PREFIX}${encodeURIComponent(slug)}${query ? `?${query}` : ''}`;
}

export function readLandingPreviewParam(search: string): string | null {
  return new URLSearchParams(search).get('landingPreview');
}
